import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Task, TaskFilters, TaskStats, TaskCreateDTO, TaskUpdateDTO, TaskStatus } from '../types/tasks.types';
import { TaskResponse } from '../api/tasks-api';

export interface TasksState {
  tasks: Task[];
  currentTask: Task | null;
  stats: TaskStats | null;
  overdueTasks: Task[];
  loading: boolean;
  isUpdating: boolean; // Para operaciones individuales (update, delete, change status)
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  filters: TaskFilters;
}

const initialState: TasksState = {
  tasks: [],
  currentTask: null,
  stats: null,
  overdueTasks: [],
  loading: false,
  isUpdating: false,
  error: null,
  pagination: null,
  filters: {},
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    // Fetch tasks
    fetchTasksRequest: (state, action: PayloadAction<TaskFilters | undefined>) => {
      state.loading = true;
      state.error = null;
      if (action.payload) {
        state.filters = { ...state.filters, ...action.payload };
      }
    },
    fetchTasksSuccess: (state, action: PayloadAction<TaskResponse>) => {
      state.loading = false;
      state.tasks = action.payload.data || [];
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
    },
    fetchTasksFailure: (state, action: PayloadAction<string | undefined>) => {
      state.loading = false;
      state.error = action.payload || 'Error al cargar tareas';
    },

    // Fetch single task
    fetchTaskByIdRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchTaskByIdSuccess: (state, action: PayloadAction<Task>) => {
      state.loading = false;
      state.currentTask = action.payload;
    },
    fetchTaskByIdFailure: (state, action: PayloadAction<string | undefined>) => {
      state.loading = false;
      state.error = action.payload || 'Error al cargar la tarea';
    },

    // Create task
    createTaskRequest: (state, _action: PayloadAction<{ data: TaskCreateDTO; desiredStatus?: TaskStatus }>) => {
      state.loading = true;
      state.error = null;
    },
    createTaskSuccess: (state, action: PayloadAction<Task>) => {
      state.loading = false;
      // Asegurar que tasks es un array antes de hacer unshift
      if (!state.tasks) {
        state.tasks = [];
      }
      state.tasks.unshift(action.payload);
      state.currentTask = action.payload;
    },
    createTaskFailure: (state, action: PayloadAction<string | undefined>) => {
      state.loading = false;
      state.error = action.payload || 'Error al crear la tarea';
    },

    // Update task
    updateTaskRequest: (state, _action: PayloadAction<{ id: string; data: TaskUpdateDTO }>) => {
      state.isUpdating = true;
      state.error = null;
    },
    updateTaskSuccess: (state, action: PayloadAction<Task>) => {
      state.isUpdating = false;
      // Asegurar que tasks es un array
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    },
    updateTaskFailure: (state, action: PayloadAction<string | undefined>) => {
      state.isUpdating = false;
      state.error = action.payload || 'Error al actualizar la tarea';
    },

    // Delete task
    deleteTaskRequest: (state, _action: PayloadAction<string>) => {
      state.isUpdating = true;
      state.error = null;
    },
    deleteTaskSuccess: (state, action: PayloadAction<string>) => {
      state.isUpdating = false;
      // Asegurar que tasks es un array
      if (!state.tasks) {
        state.tasks = [];
      }
      state.tasks = state.tasks.filter(task => task.id !== action.payload);
      if (state.currentTask?.id === action.payload) {
        state.currentTask = null;
      }
    },
    deleteTaskFailure: (state, action: PayloadAction<string | undefined>) => {
      state.isUpdating = false;
      state.error = action.payload || 'Error al eliminar la tarea';
    },

    // Change status
    changeTaskStatusRequest: (state, action: PayloadAction<{ id: string; status: TaskStatus }>) => {
      // NO activar loading para no mostrar skeleton
      // Actualización optimista: cambiar el estado inmediatamente
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = {
          ...state.tasks[index],
          status: action.payload.status,
        };
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = {
          ...state.currentTask,
          status: action.payload.status,
        };
      }
      state.error = null;
    },
    changeTaskStatusSuccess: (state, action: PayloadAction<Task>) => {
      // Confirmar la actualización desde el servidor
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    },
    changeTaskStatusFailure: (state, action: PayloadAction<string | undefined>) => {
      // En caso de error, revertir sería ideal pero requeriría guardar el estado anterior
      // Por ahora solo mostramos el error
      state.error = action.payload || 'Error al cambiar el estado';
    },

    // Stats
    fetchStatsRequest: (state) => {
      state.error = null;
    },
    fetchStatsSuccess: (state, action: PayloadAction<TaskStats>) => {
      state.stats = action.payload;
    },
    fetchStatsFailure: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload || 'Error al obtener estadísticas';
    },

    // Overdue tasks
    fetchOverdueTasksRequest: (state) => {
      state.error = null;
    },
    fetchOverdueTasksSuccess: (state, action: PayloadAction<Task[]>) => {
      state.overdueTasks = action.payload;
    },
    fetchOverdueTasksFailure: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload || 'Error al obtener tareas vencidas';
    },

    // Change priority
    changeTaskPriorityRequest: (state, action: PayloadAction<{ id: string; priority: string }>) => {
      // Actualización optimista
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = {
          ...state.tasks[index],
          priority: action.payload.priority as any,
        };
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = {
          ...state.currentTask,
          priority: action.payload.priority as any,
        };
      }
      state.error = null;
    },
    changeTaskPrioritySuccess: (state, action: PayloadAction<Task>) => {
      // Confirmar la actualización desde el servidor
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    },
    changeTaskPriorityFailure: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload || 'Error al cambiar la prioridad';
    },

    // Reassign
    reassignTaskRequest: (state, _action: PayloadAction<{ id: string; assignedToId: string }>) => {
      state.isUpdating = true;
      state.error = null;
    },
    reassignTaskSuccess: (state, action: PayloadAction<Task>) => {
      state.isUpdating = false;
      // Asegurar que tasks es un array
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.id === action.payload.id) {
        state.currentTask = action.payload;
      }
    },
    reassignTaskFailure: (state, action: PayloadAction<string | undefined>) => {
      state.isUpdating = false;
      state.error = action.payload || 'Error al reasignar la tarea';
    },

    // Filters & helpers
    setFilters: (state, action: PayloadAction<TaskFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentTask: (state, action: PayloadAction<Task | null>) => {
      state.currentTask = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearTasks: (state) => {
      state.tasks = [];
      state.currentTask = null;
      state.stats = null;
      state.pagination = null;
    },
  },
});

export const {
  fetchTasksRequest,
  fetchTasksSuccess,
  fetchTasksFailure,
  fetchTaskByIdRequest,
  fetchTaskByIdSuccess,
  fetchTaskByIdFailure,
  createTaskRequest,
  createTaskSuccess,
  createTaskFailure,
  updateTaskRequest,
  updateTaskSuccess,
  updateTaskFailure,
  deleteTaskRequest,
  deleteTaskSuccess,
  deleteTaskFailure,
  changeTaskStatusRequest,
  changeTaskStatusSuccess,
  changeTaskStatusFailure,
  fetchStatsRequest,
  fetchStatsSuccess,
  fetchStatsFailure,
  fetchOverdueTasksRequest,
  fetchOverdueTasksSuccess,
  fetchOverdueTasksFailure,
  changeTaskPriorityRequest,
  changeTaskPrioritySuccess,
  changeTaskPriorityFailure,
  reassignTaskRequest,
  reassignTaskSuccess,
  reassignTaskFailure,
  setFilters,
  clearFilters,
  setCurrentTask,
  clearError,
  clearTasks,
} = tasksSlice.actions;

export default tasksSlice.reducer;
